/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { Select } from '@base-ui/react/select';

import { ModernIcon } from '../ModernIcon';
import { useFiltersState } from '../../FiltersContext';
import { type BookingType } from '../../FiltersContext';
import { IconBadge, type IconBadgeName } from '../../IconBadge/IconBadge';

import styles from './BookingTypeSelect.module.scss';
import sharedStyles from '../sharedStyles.module.scss';

interface BookingTypeSelectProps {
    isDisabled?: boolean;
}

type SelectOption = {
    value: BookingType;
    label: string;
    description: string;
    icon: IconBadgeName;
};

function trackHomepageMapEvent({ action }: { action: string }) {
    window.dataLayer?.push({
        event: 'map_interaction',
        category: 'Homepage',
        action,
        label: 'Homepage Map',
    });
}

const bookingTypes: SelectOption[] = [
    {
        value: 'camping',
        label: 'Camping & Lodging',
        description: 'Tent or RV sites, cabins, moorings',
        icon: 'tent',
    },
    {
        value: 'tickets',
        label: 'Tickets & Timed Entry',
        description: 'Tours, timed activities, timed access',
        icon: 'ticket',
    },
    {
        value: 'permitsAndPasses',
        label: 'Permits & Passes',
        description: 'Site access, boating, climbing, hunting, tree cutting',
        icon: 'card',
    },
    {
        value: 'venuesAndDayUse',
        label: 'Venues & Day Use',
        description: 'Reservable picnic areas, sports fields, event spaces',
        icon: 'picnic-table',
    },
    {
        value: 'nonReservable',
        label: 'Recreation Areas',
        description: 'Non-reservable parks and points of interest',
        icon: 'tree',
    },
] as const;

export function BookingTypeSelect({ isDisabled }: BookingTypeSelectProps) {
    const [filters, setFilters] = useFiltersState();
    const [value, setValue] = React.useState<SelectOption | null>(
        () => bookingTypes.find((type) => type.value === filters.bookingType) ?? null
    );

    return (
        <Select.Root
            disabled={isDisabled}
            value={value}
            onValueChange={(newValue) => {
                if (!newValue || newValue?.value === value?.value) {
                    trackHomepageMapEvent({
                        action: 'Filtering by: All Experiences',
                    });
                    setValue(null);
                    setFilters({ bookingType: null });
                } else {
                    trackHomepageMapEvent({
                        action: `Filtering by: ${newValue?.label}`,
                    });
                    setValue(newValue);
                    setFilters({ bookingType: newValue?.value });
                }
            }}
        >
            <Select.Trigger
                className={sharedStyles.triggerButton}
                aria-label="Booking type"
            >
                <Select.Value>
                    {(bookingType: SelectOption | null) =>
                        bookingType?.label ?? 'All Experiences'
                    }
                </Select.Value>
            </Select.Trigger>
            <Select.Portal>
                <Select.Positioner
                    className={styles.Positioner}
                    sideOffset={8}
                    align="center"
                    side="bottom"
                    alignItemWithTrigger={false}
                >
                    <Select.Popup className={sharedStyles.Popup}>
                        <Select.ScrollUpArrow className={styles.ScrollArrow} />
                        <Select.List className={sharedStyles.List}>
                            {bookingTypes.map((item) => {
                                return (
                                    <Select.Item
                                        key={item.value}
                                        value={item}
                                        className={styles.Item}
                                    >
                                        <IconBadge iconName={item.icon} />
                                        <Select.ItemText className={styles.ItemText}>
                                            <span className={styles.ItemLabel}>
                                                {item.label}
                                            </span>
                                            <span className={styles.ItemDescription}>
                                                {item.description}
                                            </span>
                                        </Select.ItemText>
                                        <Select.ItemIndicator
                                            className={styles.ItemIndicator}
                                        >
                                            <ModernIcon
                                                iconName="check"
                                                absoluteStrokeWidth={true}
                                                strokeWidth={2}
                                                className={styles.ItemIndicatorIcon}
                                            />
                                        </Select.ItemIndicator>
                                    </Select.Item>
                                );
                            })}
                        </Select.List>
                        <Select.ScrollDownArrow className={styles.ScrollArrow} />
                    </Select.Popup>
                </Select.Positioner>
            </Select.Portal>
        </Select.Root>
    );
}
